// src/types.ts
var RampProviderId = /* @__PURE__ */ ((RampProviderId2) => {
  RampProviderId2["Transak"] = "transak";
  RampProviderId2["MtPelerin"] = "mtPelerin";
  RampProviderId2["Coinbase"] = "coinbase";
  RampProviderId2["Wert"] = "wert";
  return RampProviderId2;
})(RampProviderId || {});
var SUPPORTED_PROVIDERS = [
  "transak" /* Transak */,
  "mtPelerin" /* MtPelerin */,
  "coinbase" /* Coinbase */,
  "wert" /* Wert */
];
var RampOperation = /* @__PURE__ */ ((RampOperation2) => {
  RampOperation2["BUY"] = "BUY";
  RampOperation2["SELL"] = "SELL";
  RampOperation2["LOGOUT"] = "LOGOUT";
  return RampOperation2;
})(RampOperation || {});

// src/providers/coinbase.ts
import { getOnrampBuyUrl } from "@coinbase/onchainkit/fund";

// src/networks.ts
var NETWORK_MAPPINGS = {
  transak: {
    tezos: "mainnet",
    mainnet: "mainnet",
    etherlink: "etherlink",
    ethereum: "ethereum"
  },
  mtPelerin: {
    tezos: "tezos_mainnet",
    mainnet: "tezos_mainnet",
    ethereum: "mainnet"
  },
  coinbase: {
    tezos: "tezos",
    mainnet: "tezos",
    ethereum: "ethereum"
  },
  wert: {
    tezos: "tezos",
    ethereum: "ethereum",
    etherlink: "etherlink"
  }
};
var getSupportedNetworksForProvider = (provider) => Object.keys(NETWORK_MAPPINGS[provider]);
var isNetworkSupportedByProvider = (network, provider) => network in NETWORK_MAPPINGS[provider];
var isSupportedNetwork = (network) => Object.values(NETWORK_MAPPINGS).some((providerMappings) => network in providerMappings);
var mapNetworkToProvider = (network, provider) => NETWORK_MAPPINGS[provider][network];

// src/providers/baseProvider.ts
var RampProviderError = class extends Error {
  constructor(message, provider, statusCode, response) {
    super(message);
    this.provider = provider;
    this.statusCode = statusCode;
    this.response = response;
    this.name = "RampProviderError";
  }
};
var BaseRampProvider = class _BaseRampProvider {
  static FIAT_DECIMALS = 2;
  static CRYPTO_DECIMALS = 6;
  /**
   * Map the network to provider-specific network identifier
   * @param network - The network to map
   * @returns true if the network is supported and mapped, false otherwise
   */
  mapNetwork(request) {
    if (!request.network) {
      throw new RampProviderError("Network is required", this.id);
    }
    const mappedNetwork = mapNetworkToProvider(request.network, this.id);
    if (mappedNetwork === void 0) {
      throw new RampProviderError(
        // We don't want to show toast error here
        "",
        this.id
      );
    }
    request.network = mappedNetwork;
    return true;
  }
  /**
   * Get the exchange screen title based on the operation
   * @param operation - The ramp operation
   * @param currency - The currency being traded
   */
  getExchangeScreenTitle(operation, currency) {
    switch (operation) {
      case "BUY" /* BUY */:
        return `Buy ${currency}`;
      case "SELL" /* SELL */:
        return `Sell ${currency}`;
      case "LOGOUT" /* LOGOUT */:
        return "Logout";
      default:
        throw new RampProviderError(`Invalid operation: ${operation}`, this.id);
    }
  }
  /**
   * Format a number to a string with the specified number of decimal places
   * @param value - The number to format
   * @param decimals - The number of decimal places (defaults to FIAT_DECIMALS)
   */
  formatNumber(value, decimals = _BaseRampProvider.FIAT_DECIMALS) {
    return value.toFixed(decimals);
  }
};

// src/env.ts
import { getEnv } from "@umami/utils";
var TRANSAK_BASE_QUOTE_URL = getEnv("VITE_TRANSAK_BASE_QUOTE_URL");
var TRANSAK_BASE_EXCHANGE_URL = getEnv("VITE_TRANSAK_BASE_EXCHANGE_URL");
var API_KEY = getEnv("VITE_TRANSAK_API_KEY") ?? "";
var MTP_BASE_QUOTE_URL = getEnv("VITE_MTP_BASE_QUOTE_URL");
var MTP_WIDGET_URL = getEnv("VITE_MTP_WIDGET_URL");
var MTP_API_KEY = getEnv("VITE_MTP_API_KEY");
var COINBASE_PROJECT_ID = getEnv("VITE_COINBASE_PROJECT_ID");
var PRICE_SERVICE_URL = getEnv("VITE_PRICING_API_URL");
var WERT_API_KEY = getEnv("VITE_WERT_API_KEY");
var WERT_PARTNER_ID = getEnv("VITE_WERT_PARTNER_ID");
var WERT_BASE_URL = getEnv("VITE_WERT_BASE_URL") ?? "";
var WERT_SANDBOX_URL = getEnv("VITE_WERT_SANDBOX_URL") ?? "";

// src/providers/coinbase.ts
var CoinbaseProvider = class extends BaseRampProvider {
  id = "coinbase" /* Coinbase */;
  name = "Coinbase";
  async getQuote(request) {
    try {
      this.validateRequest(request);
      const res = await fetch(`${PRICE_SERVICE_URL}/coinbase/quote`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          walletAddress: request.walletAddress,
          cryptoCurrency: request.cryptoCurrency,
          fiatCurrency: request.fiatCurrency,
          amount: request.fiatAmount,
          network: request.network
        })
      });
      const data = await res.json();
      if (!res.ok) {
        throw new RampProviderError(
          `Coinbase quote failed: ${data.error || res.statusText}`,
          this.id,
          res.status
        );
      }
      if (!data?.payment_total?.value || !data?.purchase_amount?.value) {
        throw new RampProviderError("Invalid response format from Coinbase API", this.id);
      }
      return {
        provider: this.id,
        fiatAmount: Number(data.payment_total.value),
        cryptoAmount: Number(data.purchase_amount.value),
        currency: request.cryptoCurrency,
        estimatedNetworkFee: Number(data.network_fee?.value ?? 0),
        network: request.network,
        paymentMethod: "credit_debit_card",
        feeAmount: Number(data.coinbase_fee?.value ?? 0) + Number(data.network_fee?.value ?? 0)
      };
    } catch (error) {
      if (error instanceof RampProviderError) {
        throw error;
      }
      throw new RampProviderError(
        error instanceof Error ? error.message : "Unknown error occurred",
        this.id
      );
    }
  }
  generateWidgetUrl(request) {
    this.validateRequest(request);
    const onrampBuyUrl = getOnrampBuyUrl({
      projectId: COINBASE_PROJECT_ID || "",
      addresses: { [request.walletId]: [request.network ?? ""] },
      assets: [request.currency],
      presetFiatAmount: request.fiatAmount ?? 0,
      // TODO: add fiat currency from geolocation
      fiatCurrency: "USD",
      redirectUrl: window.location.origin
    });
    return onrampBuyUrl;
  }
  validateRequest(request) {
    if (request.provider !== this.id) {
      throw new RampProviderError(
        `Invalid provider: ${request.provider}. Expected ${this.id}`,
        this.id
      );
    }
    if ("operation" in request) {
      if (!request.walletId) {
        throw new RampProviderError("Wallet ID is required", this.id);
      }
      if (!request.currency) {
        throw new RampProviderError("Currency is required", this.id);
      }
    } else {
      if (!request.cryptoCurrency) {
        throw new RampProviderError("Crypto currency is required", this.id);
      }
      if (!request.fiatCurrency) {
        throw new RampProviderError("Fiat currency is required", this.id);
      }
      if (!request.network) {
        throw new RampProviderError("Network is required", this.id);
      }
      if (!request.walletAddress) {
        throw new RampProviderError("Wallet address is required", this.id);
      }
    }
    if (!request.network) {
      throw new RampProviderError("Network is required", this.id);
    }
    this.mapNetwork(request);
  }
};
var coinbaseProvider = new CoinbaseProvider();
var CoinbaseSDKEvent = /* @__PURE__ */ ((CoinbaseSDKEvent2) => {
  CoinbaseSDKEvent2["WIDGET_CLOSE"] = "WIDGET_CLOSE";
  CoinbaseSDKEvent2["WIDGET_LOADED"] = "WIDGET_LOADED";
  CoinbaseSDKEvent2["TRANSACTION_INITIATED"] = "TRANSACTION_INITIATED";
  CoinbaseSDKEvent2["TRANSACTION_FAILED"] = "TRANSACTION_FAILED";
  CoinbaseSDKEvent2["TRANSACTION_COMPLETED"] = "TRANSACTION_COMPLETED";
  return CoinbaseSDKEvent2;
})(CoinbaseSDKEvent || {});

// src/providers/mtPelerin.ts
var MtPelerinProvider = class extends BaseRampProvider {
  id = "mtPelerin" /* MtPelerin */;
  name = "MtPelerin";
  async getQuote(request) {
    try {
      this.validateRequest(request);
      const { fiatCurrency, cryptoCurrency, fiatAmount, cryptoAmount, network } = request;
      const isBuy = !!fiatAmount;
      const amountField = isBuy ? "sourceAmount" : "destAmount";
      const amountValue = isBuy ? fiatAmount : cryptoAmount;
      const body = {
        sourceCurrency: fiatCurrency,
        destCurrency: cryptoCurrency,
        sourceNetwork: "fiat",
        destNetwork: network,
        [amountField]: amountValue
      };
      const res = await fetch(MTP_BASE_QUOTE_URL ?? "", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json"
        },
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        throw new RampProviderError(
          `MtPelerin quote failed: ${res.status} ${res.statusText}`,
          this.id,
          res.status
        );
      }
      const data = await res.json();
      if (!data.sourceAmount || !data.destAmount || !data.destCurrency) {
        throw new RampProviderError("Invalid response format from MtPelerin API", this.id);
      }
      return {
        provider: this.id,
        fiatAmount: Number(data.sourceAmount),
        cryptoAmount: Number(
          this.formatNumber(Number(data.destAmount), BaseRampProvider.CRYPTO_DECIMALS)
        ),
        currency: data.destCurrency,
        estimatedNetworkFee: Number(data.fees?.networkFee ?? 0),
        network,
        paymentMethod: "card",
        feeAmount: Number(data.fees?.fixFee ?? 0)
      };
    } catch (error) {
      if (error instanceof RampProviderError) {
        throw error;
      }
      throw new RampProviderError(
        error instanceof Error ? error.message : "Unknown error occurred",
        this.id
      );
    }
  }
  generateWidgetUrl(request) {
    this.validateRequest(request);
    const params = new URLSearchParams({
      direction: request.operation.toLowerCase(),
      addr: request.walletId,
      bdc: request.currency,
      // TODO: add fiat currency from geolocation
      bsc: "USD",
      tabs: "buy",
      ...request.fiatAmount && { bsa: request.fiatAmount.toString() },
      ...request.email && { email: request.email },
      dnet: request.network ?? "",
      _ctkn: MTP_API_KEY ?? ""
    });
    return `${MTP_WIDGET_URL}?${params.toString()}`;
  }
  validateRequest(request) {
    if (request.provider !== this.id) {
      throw new RampProviderError(
        `Invalid provider: ${request.provider}. Expected ${this.id}`,
        this.id
      );
    }
    if ("operation" in request) {
      if (!request.walletId) {
        throw new RampProviderError("Wallet ID is required", this.id);
      }
      if (!request.currency) {
        throw new RampProviderError("Currency is required", this.id);
      }
      if (request.operation !== "BUY" /* BUY */ && request.operation !== "SELL" /* SELL */) {
        throw new RampProviderError(`Invalid operation: ${request.operation}`, this.id);
      }
    } else {
      if (!request.cryptoCurrency) {
        throw new RampProviderError("Crypto currency is required", this.id);
      }
      if (!request.fiatCurrency) {
        throw new RampProviderError("Fiat currency is required", this.id);
      }
      if (!request.network) {
        throw new RampProviderError("Network is required", this.id);
      }
      if (!request.fiatAmount && !request.cryptoAmount) {
        throw new RampProviderError("Either fiat amount or crypto amount is required", this.id);
      }
      if (!this.mapNetwork(request)) {
        throw new RampProviderError("Invalid network", this.id);
      }
    }
  }
};
var mtPelerinProvider = new MtPelerinProvider();

// src/providers/transak.ts
var TransakProvider = class extends BaseRampProvider {
  id = "transak" /* Transak */;
  name = "Transak";
  async getQuote(request) {
    try {
      this.validateRequest(request);
      const params = new URLSearchParams({
        partnerApiKey: API_KEY,
        fiatCurrency: request.fiatCurrency,
        cryptoCurrency: request.cryptoCurrency,
        isBuyOrSell: "BUY",
        network: request.network,
        ...request.fiatAmount && { fiatAmount: this.formatNumber(request.fiatAmount) },
        ...request.cryptoAmount && {
          cryptoAmount: this.formatNumber(request.cryptoAmount, BaseRampProvider.CRYPTO_DECIMALS)
        }
      });
      const url = `${TRANSAK_BASE_QUOTE_URL}?${params.toString()}`;
      const res = await fetch(url, {
        headers: {
          Accept: "application/json"
        }
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new RampProviderError(
          `Transak quote failed: ${errorData.error.message || "Unknown error"}`,
          this.id,
          res.status
        );
      }
      const { response } = await res.json();
      return {
        provider: this.id,
        fiatAmount: Number(response.fiatAmount),
        cryptoAmount: Number(response.cryptoAmount),
        currency: response.cryptoCurrency,
        estimatedNetworkFee: Number(response.feeAmount || 0),
        network: response.network,
        paymentMethod: response.paymentMethod,
        feeAmount: Number(response.totalFee || 0)
      };
    } catch (error) {
      if (error instanceof RampProviderError) {
        throw error;
      }
      throw new RampProviderError(
        error instanceof Error ? error.message : "Unknown error occurred",
        this.id
      );
    }
  }
  generateWidgetUrl(request) {
    this.validateRequest(request);
    const params = new URLSearchParams({
      apiKey: API_KEY,
      exchangeScreenTitle: this.getExchangeScreenTitle(request.operation, request.currency),
      walletAddress: request.walletId,
      productsAvailed: request.operation,
      defaultCryptoCurrency: request.currency,
      network: request.network ?? "etherlink",
      fiatAmount: request.fiatAmount?.toString() ?? "",
      // TODO: add fiat currency from geolocation
      defaultFiatCurrency: "USD",
      fiatCurrency: "USD",
      cryptoCurrencyCode: request.currency,
      email: request.email ?? "",
      disableWalletAddressForm: "true",
      hideMenu: "false"
    });
    return `${TRANSAK_BASE_EXCHANGE_URL}?${params.toString()}`;
  }
  validateRequest(request) {
    if (request.provider !== this.id) {
      throw new RampProviderError(
        `Invalid provider: ${request.provider}. Expected ${this.id}`,
        this.id
      );
    }
    if ("operation" in request) {
      if (!request.walletId) {
        throw new RampProviderError("Wallet ID is required", this.id);
      }
      if (!request.currency) {
        throw new RampProviderError("Currency is required", this.id);
      }
    } else {
      if (!request.cryptoCurrency) {
        throw new RampProviderError("Crypto currency is required", this.id);
      }
      if (!request.fiatCurrency) {
        throw new RampProviderError("Fiat currency is required", this.id);
      }
      if (!request.network) {
        throw new RampProviderError("Network is required", this.id);
      }
      if (!request.fiatAmount && !request.cryptoAmount) {
        throw new RampProviderError("Either fiat amount or crypto amount is required", this.id);
      }
    }
    if (!request.network) {
      throw new RampProviderError("Network is required", this.id);
    }
    if (!this.mapNetwork(request)) {
      throw new RampProviderError("", this.id);
    }
  }
};
var transakProvider = new TransakProvider();
var TransakSDKEvent = /* @__PURE__ */ ((TransakSDKEvent2) => {
  TransakSDKEvent2["TRANSAK_WIDGET_CLOSE"] = "TRANSAK_WIDGET_CLOSE";
  TransakSDKEvent2["TRANSAK_WIDGET_INITIALISED"] = "TRANSAK_WIDGET_INITIALISED";
  TransakSDKEvent2["TRANSAK_WIDGET_OPEN"] = "TRANSAK_WIDGET_OPEN";
  TransakSDKEvent2["TRANSAK_ORDER_CREATED"] = "TRANSAK_ORDER_CREATED";
  TransakSDKEvent2["TRANSAK_ORDER_CANCELLED"] = "TRANSAK_ORDER_CANCELLED";
  TransakSDKEvent2["TRANSAK_ORDER_FAILED"] = "TRANSAK_ORDER_FAILED";
  TransakSDKEvent2["TRANSAK_ORDER_SUCCESSFUL"] = "TRANSAK_ORDER_SUCCESSFUL";
  return TransakSDKEvent2;
})(TransakSDKEvent || {});

// src/providers/wert.ts
var WertProvider = class extends BaseRampProvider {
  id = "wert" /* Wert */;
  name = "Wert";
  async getQuote(request) {
    try {
      this.validateRequest(request);
      const quoteRequest = {
        from: request.fiatCurrency,
        network: request.network,
        to: request.cryptoCurrency,
        amount: request.fiatAmount || 0
      };
      const isSandbox = WERT_BASE_URL.includes("sandbox") || false;
      const baseUrl = isSandbox ? WERT_SANDBOX_URL || "https://sandbox.wert.io" : WERT_BASE_URL || "https://widget.wert.io";
      const quoteUrl = `${baseUrl}/api/v3/partners/convert`;
      const res = await fetch(quoteUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Partner-ID": WERT_PARTNER_ID || ""
        },
        body: JSON.stringify(quoteRequest)
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new RampProviderError(
          `Wert quote failed: ${errorData.message || "Unknown error"}`,
          this.id,
          res.status
        );
      }
      const data = await res.json();
      if (data.status !== "ok") {
        throw new RampProviderError(`Wert quote failed: ${data.status}`, this.id);
      }
      return {
        provider: this.id,
        fiatAmount: data.body.currency_amount,
        cryptoAmount: data.body.commodity_amount,
        currency: request.cryptoCurrency,
        estimatedNetworkFee: data.body.currency_miner_fee,
        network: request.network,
        paymentMethod: "card",
        feeAmount: data.body.fee_amount
      };
    } catch (error) {
      if (error instanceof RampProviderError) {
        throw error;
      }
      throw new RampProviderError(
        error instanceof Error ? error.message : "Unknown error occurred",
        this.id
      );
    }
  }
  async createSession(request) {
    this.validateRequest(request);
    const response = await fetch(
      `${process.env.VITE_PRICING_API_URL || "http://localhost:3001"}/wert/session`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          walletAddress: request.walletId,
          commodity: request.currency,
          network: request.network || "tezos",
          currency: "USD",
          currencyAmount: request.fiatAmount || 100
        })
      }
    );
    if (!response.ok) {
      const errorData = await response.json();
      throw new RampProviderError(
        `Wert session creation failed: ${errorData.error || "Unknown error"}`,
        this.id,
        response.status
      );
    }
    return await response.json();
  }
  generateWidgetUrl(request) {
    this.validateRequest(request);
    const isSandbox = WERT_BASE_URL.includes("sandbox") || false;
    const baseUrl = isSandbox ? WERT_SANDBOX_URL || "https://sandbox.wert.io" : WERT_BASE_URL || "https://widget.wert.io";
    const params = new URLSearchParams({
      partner_id: WERT_PARTNER_ID || "",
      wallet_address: request.walletId,
      commodity: request.currency,
      network: request.network || "tezos",
      currency: "USD",
      currency_amount: (request.fiatAmount || 100).toString(),
      flow_type: "simple_full_restrict",
      is_sandbox: isSandbox.toString()
    });
    return `${baseUrl}/widget?${params.toString()}`;
  }
  validateRequest(request) {
    if (request.provider !== this.id) {
      throw new RampProviderError(
        `Invalid provider: ${request.provider}. Expected ${this.id}`,
        this.id
      );
    }
    if ("operation" in request) {
      if (!request.walletId) {
        throw new RampProviderError("Wallet ID is required", this.id);
      }
      if (!request.currency) {
        throw new RampProviderError("Currency is required", this.id);
      }
    } else {
      if (!request.cryptoCurrency) {
        throw new RampProviderError("Crypto currency is required", this.id);
      }
      if (!request.fiatCurrency) {
        throw new RampProviderError("Fiat currency is required", this.id);
      }
      if (!request.network) {
        throw new RampProviderError("Network is required", this.id);
      }
      if (!request.fiatAmount && !request.cryptoAmount) {
        throw new RampProviderError("Either fiat amount or crypto amount is required", this.id);
      }
    }
    if (!request.network) {
      throw new RampProviderError("Network is required", this.id);
    }
    if (!this.mapNetwork(request)) {
      throw new RampProviderError("", this.id);
    }
  }
};
var wertProvider = new WertProvider();
var WertSDKEvent = /* @__PURE__ */ ((WertSDKEvent2) => {
  WertSDKEvent2["WIDGET_CLOSE"] = "WIDGET_CLOSE";
  WertSDKEvent2["WIDGET_INITIALISED"] = "WIDGET_INITIALISED";
  WertSDKEvent2["WIDGET_OPEN"] = "WIDGET_OPEN";
  WertSDKEvent2["ORDER_CREATED"] = "ORDER_CREATED";
  WertSDKEvent2["ORDER_CANCELLED"] = "ORDER_CANCELLED";
  WertSDKEvent2["ORDER_FAILED"] = "ORDER_FAILED";
  WertSDKEvent2["ORDER_SUCCESSFUL"] = "ORDER_SUCCESSFUL";
  return WertSDKEvent2;
})(WertSDKEvent || {});

// src/providers/factory.ts
var RampProviderFactory = class _RampProviderFactory {
  static instance;
  providers;
  constructor() {
    this.providers = /* @__PURE__ */ new Map();
    this.initializeProviders();
  }
  initializeProviders() {
    this.providers.set("transak" /* Transak */, new TransakProvider());
    this.providers.set("mtPelerin" /* MtPelerin */, new MtPelerinProvider());
    this.providers.set("coinbase" /* Coinbase */, new CoinbaseProvider());
    this.providers.set("wert" /* Wert */, new WertProvider());
  }
  static getInstance() {
    if (!_RampProviderFactory.instance) {
      _RampProviderFactory.instance = new _RampProviderFactory();
    }
    return _RampProviderFactory.instance;
  }
  getProvider(id) {
    const provider = this.providers.get(id);
    if (!provider) {
      throw new Error(`Provider ${id} not found`);
    }
    return provider;
  }
  getAllProviders() {
    return Array.from(this.providers.values());
  }
};
var transakProvider2 = RampProviderFactory.getInstance().getProvider(
  "transak" /* Transak */
);
var mtPelerinProvider2 = RampProviderFactory.getInstance().getProvider(
  "mtPelerin" /* MtPelerin */
);
var coinbaseProvider2 = RampProviderFactory.getInstance().getProvider(
  "coinbase" /* Coinbase */
);
var wertProvider2 = RampProviderFactory.getInstance().getProvider("wert" /* Wert */);

// src/providers/types.ts
var providerMap = {
  ["transak" /* Transak */]: transakProvider2,
  ["mtPelerin" /* MtPelerin */]: mtPelerinProvider2,
  ["coinbase" /* Coinbase */]: coinbaseProvider2,
  ["wert" /* Wert */]: wertProvider2
};

// src/quotes.ts
var getQuote = async (request) => {
  const provider = providerMap[request.provider];
  return provider.getQuote(request);
};

// src/widgets.ts
var generateWidgetUrl = (request) => {
  const provider = providerMap[request.provider];
  return provider.generateWidgetUrl(request);
};
export {
  BaseRampProvider,
  CoinbaseSDKEvent,
  RampOperation,
  RampProviderError,
  RampProviderFactory,
  RampProviderId,
  SUPPORTED_PROVIDERS,
  TransakSDKEvent,
  WertProvider,
  WertSDKEvent,
  coinbaseProvider2 as coinbaseProvider,
  generateWidgetUrl,
  getQuote,
  getSupportedNetworksForProvider,
  isNetworkSupportedByProvider,
  isSupportedNetwork,
  mapNetworkToProvider,
  mtPelerinProvider2 as mtPelerinProvider,
  providerMap,
  transakProvider2 as transakProvider,
  wertProvider2 as wertProvider
};
//# sourceMappingURL=index.js.map